import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { WorkOrderActKc2Data } from 'gql/graphql';
import { CostGroupEnum } from 'gql/graphql';
import { getDefaultFilterCleaner, usePageFilterSaver, withDefaultTransformer } from 'hooks/usePageFilterSaver';

import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { destroyWorkOrderFilters, getWorkOrderWhereFromFilter } from '../../WorkOrderPrice/hooks';
import { workOrderPriceActKC2Gql } from '../../workOrderQueries';
import type { rowsWithGroupTitle } from '../otherHelpers';
import { getGroupedRows } from '../otherHelpers';
import { getWorkOrderOtherData } from '../workOrderOtherTableQueries';

export type useGetWorkOrderOtherDataGetterTypes = {
    tableItems: rowsWithGroupTitle[];
    totalItems: number | undefined;
    fetchedItems: number;
    isLoading: boolean;
    workOrderActsList: WorkOrderActKc2Data[];
    reFetchData: () => void;
    reFetchActsData: () => void;
    changeFilterHandler: ({ fieldName, checkedValues }: { fieldName: string; checkedValues: { data: any } }) => void;
};

export const useGetWorkOrderOtherDataGetter = ({
    workOrderId,
    workOrderRootObjectId,
    withEffect = true,
    reload,
    gridApiRef,
}: {
    workOrderId: number;
    workOrderRootObjectId: number;
    gridApiRef?: AgGridReact<any> | null;
    reload?: boolean | number;
    withEffect?: boolean;
}): useGetWorkOrderOtherDataGetterTypes => {
    const dispatch = useDispatch();

    const { state }: { state: { actNavInfo?: any } } = useLocation();

    const [filtersWhere, setFiltersWhere] = useState<Record<string, any> | undefined>(undefined);

    const changeFilterHandler = useCallback(({ fieldName, checkedValues }) => {
        setFiltersWhere((prevValues) => ({
            and: {
                ...prevValues?.and,
                ...getWorkOrderWhereFromFilter(fieldName, checkedValues),
            },
        }));
    }, []);

    const getWorkOrderOtherDataVariables = useMemo(
        () => ({
            workOrderId,
            workOrderRootObjectId,
            where: filtersWhere,
        }),
        // eslint-disable-next-line
        [workOrderId, workOrderRootObjectId, filtersWhere, reload],
    );

    const workOrderPriceGetActsVariables = useMemo(
        () => ({ workOrderId, workOrderRootObjectId, actCostGroup: CostGroupEnum.Others }),
        // eslint-disable-next-line
        [workOrderId, workOrderRootObjectId, reload],
    );
    const { canSendRequest } = usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: withDefaultTransformer(
                    changeFilterHandler,
                    `others_${workOrderRootObjectId}`,
                    getDefaultFilterCleaner({ changeFilterHandler, setFiltersWhere }),
                    Boolean(state?.actNavInfo?.actId),
                ),
            }),
            [changeFilterHandler, state?.actNavInfo, workOrderRootObjectId],
        ),
    );
    const [workOrderPriceListActs, , , workOrderPriceListActsMakeRequest] = useRequest({
        requestId: 'workOrderOtherPriceListActs',
        query: workOrderPriceActKC2Gql,
        variables: workOrderPriceGetActsVariables,
        withEffect: Boolean(workOrderId && workOrderRootObjectId && withEffect && canSendRequest),
    });

    const [workOrderPriceListOtherData, workOrderPriceListOtherStatus, , workOrderPriceListOtherMakeRequest] =
        useRequest({
            requestId: 'workOrderPriceListOthers',
            query: getWorkOrderOtherData,
            variables: getWorkOrderOtherDataVariables,
            withEffect: Boolean(workOrderId && workOrderRootObjectId && withEffect && canSendRequest),
        });

    const isLoading = workOrderPriceListOtherStatus === RequestStatus.FETCHING;

    const actsKc2 = useMemo(
        () => workOrderPriceListActs?.uerWorkOrderActsKC2?.items ?? [],
        [workOrderPriceListActs?.uerWorkOrderActsKC2?.items],
    );

    const otherData = useMemo(
        () => workOrderPriceListOtherData?.uerWorkOrderOtherPrices?.items ?? [],
        [workOrderPriceListOtherData?.uerWorkOrderOtherPrices?.items],
    );

    const tableItems = useMemo(() => getGroupedRows(otherData, actsKc2), [actsKc2, otherData]);

    const totalItems = useMemo(
        () => workOrderPriceListOtherData?.uerWorkOrderOtherPrices?.totalCount,
        [workOrderPriceListOtherData?.uerWorkOrderOtherPrices?.totalCount],
    );

    const fetchedItems = useMemo(() => otherData.length, [otherData.length]);

    const refreshFilters = useCallback(() => {
        destroyWorkOrderFilters({
            api: gridApiRef?.api,
            columns: ['name', 'status'],
        });
    }, [gridApiRef?.api]);

    const reFetchData = useCallback(() => {
        dispatch(workOrderPriceListOtherMakeRequest(getWorkOrderOtherDataVariables));
    }, [dispatch, getWorkOrderOtherDataVariables, workOrderPriceListOtherMakeRequest]);

    const reFetchActsData = useCallback(() => {
        dispatch(workOrderPriceListActsMakeRequest(workOrderPriceGetActsVariables));
    }, [dispatch, workOrderPriceGetActsVariables, workOrderPriceListActsMakeRequest]);

    const [resetData] = useResetStoreData({ requestId: 'workOrderPriceListOthers' });
    const [resetActsData] = useResetStoreData({ requestId: 'workOrderOtherPriceListActs' });

    useEffect(() => {
        refreshFilters();
    }, [refreshFilters, reload]);

    useEffect(
        () => () => {
            refreshFilters();
            resetData(null);
            resetActsData(null);
        },
        [refreshFilters, resetActsData, resetData],
    );

    useEffect(() => {
        if (tableItems.length) {
            gridApiRef?.api?.refreshCells?.({ force: true });
        }
    }, [gridApiRef?.api, tableItems]);

    return {
        tableItems,
        totalItems,
        fetchedItems,
        isLoading,
        reFetchActsData,
        reFetchData,
        workOrderActsList: actsKc2,
        changeFilterHandler,
    };
};
